/**
 * BookDirectoryService - Manages dynamic book directory configuration
 * Communicates with backend to set the correct books path based on selected book list
 */

export interface BookDirectoryConfig {
  booksPath: string;
  bookListId: string;
  bookListName: string;
}

export class BookDirectoryService {
  private baseUrl: string;
  private currentConfig: BookDirectoryConfig | null = null;

  constructor(baseUrl: string = 'http://localhost:3001') {
    this.baseUrl = baseUrl;
  }

  /**
   * Set the books directory path (now uses FolderService - folder is per-request)
   */
  async setBooksPath(booksPath: string, bookListId: string, bookListName: string): Promise<void> {
    try {
      // Import FolderService
      const { FolderService } = await import('./FolderService');
      
      // Set folder in FolderService (this will clear caches)
      FolderService.setFolder(booksPath);

      // Store current configuration
      this.currentConfig = {
        booksPath,
        bookListId,
        bookListName
      };

      // Store in localStorage for persistence
      localStorage.setItem('book-directory-config', JSON.stringify(this.currentConfig));

    } catch (error) {
      throw error;
    }
  }

  /**
   * Get the current books directory path from FolderService
   */
  async getBooksPath(): Promise<string> {
    try {
      const { FolderService } = await import('./FolderService');
      const folder = FolderService.getFolder();
      
      if (!folder) {
        throw new Error('No folder selected. Please select a books directory first.');
      }
      
      return folder;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Configure books directory based on book list selection.
   * Does not use cached book list data; caller ensures folder is set and list is loaded when needed.
   */
  async configureFromBookList(bookListId: string, bookListName: string, bookDirectoryPath?: string): Promise<void> {
    if (!bookDirectoryPath) {
      throw new Error('Book directory path is required. Please select the directory containing your books.');
    }
    await this.setBooksPath(bookDirectoryPath, bookListId, bookListName);
  }

  /**
   * Get current configuration
   */
  getCurrentConfig(): BookDirectoryConfig | null {
    return this.currentConfig;
  }

  /**
   * Load configuration from localStorage
   */
  loadFromStorage(): BookDirectoryConfig | null {
    try {
      const configStr = localStorage.getItem('book-directory-config');
      if (configStr) {
        this.currentConfig = JSON.parse(configStr);
        return this.currentConfig;
      }
    } catch (error) {
      console.error('Error loading directory config from storage:', error);
    }
    return null;
  }

  /**
   * Clear current configuration
   */
  clearConfig(): void {
    this.currentConfig = null;
    localStorage.removeItem('book-directory-config');
  }
}

/**
 * Factory function to create BookDirectoryService instance
 */
export function createBookDirectoryService(baseUrl: string = 'http://localhost:3001'): BookDirectoryService {
  return new BookDirectoryService(baseUrl);
}

export default BookDirectoryService;
